/**
 * Typed client for the per-user harn-service sidecar at `/api/harn/*`.
 *
 * Wire chain (mirrors the lx-mission chain — see lx-mission.ts header):
 *   SPA (this file) → vite/lexserver `/api/harn/*`
 *                   → lexserver `runtime-harn-proxy.ts` (Part E shipped:
 *                     mounted at server.ts:304 with requireOperator())
 *                   → runtime gateway → openclaw plugin's `/harn/*` proxy
 *                     (PREFIX `/harn`, strips it)
 *                   → harn-service (loopback :3210) — router.ts
 *
 * SPA URLs:
 *   - GET    /api/harn/tasks            → { tasks: HarnTaskSummary[] }
 *   - GET    /api/harn/tasks/:id        → HarnTaskRecord
 *   - GET    /api/harn/tasks/:id/result → polymorphic (completed/failed/awaiting_user/409 pending)
 *   - DELETE /api/harn/tasks/:id        → { cancelled: true }
 *
 * Auth + envelope rules — same as lx-mission.ts:
 *   - `Authorization: Bearer <Lexiun JWT>` + `traceparent` per call.
 *   - harn-service returns RAW JSON (no `{ok,data}` envelope wrap; DO
 *     NOT unwrap `body.data`).
 *   - Error shape: `{error: "<code>"}` bare string (router default) or
 *     `{error: {code, message}}` object form on a few paths. The
 *     normaliser handles both — mirrors lx-mission's normaliser.
 *   - 404 on DELETE is the "conflated case" — `not_found_or_terminal`
 *     — could mean task doesn't exist OR was already terminal. The
 *     `cancelTask` helper treats it as a successful
 *     terminal-by-deletion (returns `{cancelled: false, alreadyTerminal:
 *     true}`) so the caller can distinguish.
 *
 * Used by:
 *   - `useHarnTasksStore` + `useHarnTasksPoll` (S2 of phase c) — the
 *     polling layer that backs the Team of Agents pane's standalone-
 *     harness rows.
 *   - The shell-level Team of Agents pane (S3 onward) — never the
 *     case-workspace gateway `HarnClient` (separate path, retained
 *     for the existing inline widget-liveness flows).
 *
 * Plan: ai-docs/lexide-right-sidebar-implementation-plan.md §H.1 + §J.S2.
 */
import { getToken } from "@/lib/lexiun-jwt";
import { newTraceparent } from "@/lib/traceparent";

// ─── Wire types (mirror lexruntime/harn-service/src/types.ts) ─────────

/** Open string — harn-service task lifecycle. Tasks transition
 *  through queued → dispatched → running → terminal
 *  ({completed | failed | awaiting_user}). Cancel transitions a
 *  running task to `failed` with `error.code: "cancelled"` (not a
 *  distinct status) per the parallel agent's handback §3. */
export type HarnTaskStatus =
  | "queued"
  | "dispatched"
  | "running"
  | "awaiting_user"
  | "completed"
  | "failed";

export interface HarnTaskScope {
  orgId: string;
  clientId?: string;
  matterId?: string;
  caseId?: string;
}

export interface HarnTaskProgress {
  step: string;
  pct?: number;
  message?: string;
}

export interface HarnTaskError {
  code: string;
  message: string;
}

/** The summary shape `GET /api/harn/tasks` returns (router.ts:309).
 *  The full record at `GET /api/harn/tasks/:id` carries additional
 *  fields (startedAt/completedAt/durationMs/result/error). */
export interface HarnTaskSummary {
  taskId: string;
  harnessId: string;
  /** Harness-level scope tag (e.g., "org" / "case"). String per the
   *  Part E live-validation fix (`01af0584`). */
  scope: string;
  orgId: string;
  clientId?: string;
  matterId?: string;
  caseId?: string;
  status: HarnTaskStatus;
  createdAt: number;
  progress?: HarnTaskProgress;
  /** Chain resume parent — non-null when this task continues a prior
   *  awaiting-user resume. Standalone harnesses have it undefined. */
  parentTaskId?: string;
  chainDepth?: number;
  // ── Ledger enrichment (harn-sdk response.ts:60-68) ────────────────────
  /** Resolved model id (gap H1) — stamped at setup time. */
  model?: string;
  /** Resolved provider id (gap H1) — stamped at setup time. */
  provider?: string;
  /** Rolled up from the task_usage frame (gap H2). */
  costUsd?: number;
  tokensIn?: number;
  tokensOut?: number;
  /** Human-readable run essence; used as the dialog header subtitle.
   *  The spawner (lx_delegate / planner) feeds this via the DAG node's
   *  `summary`; the backend's precedence is harness outcome > spawner
   *  intent > harness description, so it's always non-empty for
   *  ledger-v2 tasks. Pre-ledger tasks may not carry it. */
  summary?: string;
  /** Short human label (manifest tool.label) — preferred over harnessId
   *  for display. e.g. "Jurisprudence Researcher" not "search-jurisprudence". */
  title?: string;
  /**
   * Whether this run is the lawyer's work or the platform's plumbing —
   * projected from the harness manifest beside `title`
   * (`harn-service/src/task-manager.ts:611`).
   *
   * A CLOSED enum by agreement with `git-implementation` (bus thread
   * 20260806T164323Z, accepted 2026-08-06), specifically so a typo is a
   * manifest validation error at load rather than a value that parses fine
   * and silently never filters.
   *
   * ⚠ **ABSENT MEANS `user_work`, and that direction is load-bearing.** A
   * manifest predating the field, or a break anywhere in the projection,
   * must leave a real task VISIBLE to the lawyer. Fail-visible, never
   * fail-hidden — asserted in `monitor-tree.test.ts`.
   */
  classification?: "user_work" | "infrastructure";
  /** Set when this task is a mission leaf — the SPA links it back to
   *  the parent MissionDetailDialog so the lawyer can drill up. */
  missionId?: string;
  /** Notification target — present ⇔ the task was fired FROM A TURN
   *  (harn-service `task-manager.ts:110`, §6-ter). The sidebar reads
   *  `notify.sessionKey` for ATTRIBUTION: an isolated cron run's key is
   *  `agent:<id>:cron:<jobId>:run:<runId>`, so a `:cron:` segment marks
   *  a run the SCHEDULER started rather than one the lawyer asked for.
   *  Absent for mission leaves. */
  notify?: { sessionKey?: string; label?: string };
}

/** Full record — `GET /api/harn/tasks/:id` payload. */
export interface HarnTaskRecord extends HarnTaskSummary {
  startedAt?: number;
  completedAt?: number;
  durationMs?: number;
  result?: unknown;
  error?: HarnTaskError;
  /** Populated when the task is in `awaiting_user` state — the SPA
   *  reads this for the detail-dialog awaiting-user block. Per the
   *  harn-sdk schema (response.ts:74-80). The dialog never POSTs
   *  the resume token directly — answers route through the chat-
   *  mediated path (plan §B.2 / §I.5 — deferred to v1.1). */
  awaitingUser?: {
    questions: unknown[];
    resumeToken: string;
    expiresAt: number;
  };
}

/** `GET /api/harn/tasks/:id/result` polymorphic shape. 409 pending
 *  thrown as `HarnApiError(status:409, code:"pending")` so callers
 *  can keep polling (mirror lx-mission's isMissionPending pattern). */
export type HarnTaskResultResponse =
  | { ok: true; data: unknown }
  | { ok: false; error: HarnTaskError }
  | {
      ok: false;
      status: "awaiting_user";
      questions: unknown[];
      resume_token: string;
      expires_at: number;
    };

// ─── Error class (mirror MissionApiError) ─────────────────────────────

export class HarnApiError extends Error {
  status: number;
  code?: string;
  taskId?: string;
  /** Set when DELETE /tasks/:id returns 404 `not_found_or_terminal`
   *  AND the caller went through `cancelTask`. Distinguishes the
   *  conflated "task gone vs already terminal" case from a regular
   *  404 on other endpoints. */
  alreadyTerminal?: boolean;
  constructor(
    message: string,
    status: number,
    code?: string,
    taskId?: string,
  ) {
    super(message);
    this.name = "HarnApiError";
    this.status = status;
    this.code = code;
    this.taskId = taskId;
  }
}

export const isHarnTaskPending = (e: unknown): boolean =>
  e instanceof HarnApiError && e.status === 409 && e.code === "pending";

// ─── Internal helpers ─────────────────────────────────────────────────

const ROOT = "/api/harn";
const DEFAULT_TIMEOUT_MS = 8_000;

async function authedHeaders(): Promise<Record<string, string>> {
  const token = await getToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    traceparent: newTraceparent(),
  };
}

interface ErrorEnvelope {
  error?: string | { code?: string; message?: string };
  message?: string;
}

async function request<T>(
  method: "GET" | "POST" | "DELETE",
  path: string,
  opts?: {
    body?: unknown;
    timeoutMs?: number;
    taskIdForError?: string;
  },
): Promise<T> {
  const ctl = new AbortController();
  const timer = setTimeout(
    () => ctl.abort(),
    opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS,
  );
  try {
    const res = await fetch(`${ROOT}${path}`, {
      method,
      headers: await authedHeaders(),
      body: opts?.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: ctl.signal,
    });
    const raw = (await res.json().catch(() => ({}))) as ErrorEnvelope;
    if (!res.ok) {
      let code: string | undefined;
      let message: string | undefined;
      if (typeof raw.error === "string") {
        code = raw.error;
        message = raw.message;
      } else if (raw.error && typeof raw.error === "object") {
        code = raw.error.code;
        message = raw.error.message ?? raw.message;
      } else {
        message = raw.message;
      }
      throw new HarnApiError(
        message ?? `harn-service ${method} ${path} returned ${res.status}`,
        res.status,
        code,
        opts?.taskIdForError,
      );
    }
    return raw as T;
  } finally {
    clearTimeout(timer);
  }
}

// ─── Public API ───────────────────────────────────────────────────────

export interface ListTasksFilters {
  status?: HarnTaskStatus;
  caseId?: string;
  orgId?: string;
  harnessId?: string;
  /** Bounded server-side (max 500); SPA default lets the server cap. */
  limit?: number;
}

export const listTasks = (
  f: ListTasksFilters = {},
): Promise<{ tasks: HarnTaskSummary[] }> => {
  const qs = new URLSearchParams();
  if (f.status) qs.set("status", f.status);
  if (f.caseId) qs.set("caseId", f.caseId);
  if (f.orgId) qs.set("orgId", f.orgId);
  if (f.harnessId) qs.set("harnessId", f.harnessId);
  if (f.limit !== undefined) qs.set("limit", String(f.limit));
  const q = qs.toString();
  return request<{ tasks: HarnTaskSummary[] }>(
    "GET",
    `/tasks${q ? `?${q}` : ""}`,
  );
};

export const getTask = (taskId: string) =>
  request<HarnTaskRecord>("GET", `/tasks/${encodeURIComponent(taskId)}`, {
    taskIdForError: taskId,
  });

/** One line of `GET /api/harn/tasks/:id/logs` (JSONL, one object per
 *  line). Only the progress-frame fields are modelled — the endpoint
 *  also carries generic log records the sidebar ignores. */
export interface HarnTaskLogEntry {
  ts?: number;
  time?: number;
  type?: string;
  /** Progress frames put the payload either at the top level or under
   *  `progress`/`fields`, depending on the emitting SDK version. */
  step?: string;
  pct?: number;
  message?: string;
  progress?: HarnTaskProgress;
  fields?: { step?: string; pct?: number; message?: string };
}

/** `GET /api/harn/tasks/:id/logs` — the PERSISTED progress-frame history.
 *
 *  This is handoff option (a): a directly-fired harness keeps only its
 *  LATEST progress frame on the task record, so its checklist children
 *  are derived from this history instead. Costs one fetch per expanded
 *  row and needs no runtime change. Returns [] when the task has no log
 *  sink configured (`HARN_LOG_DIR` unset) — callers must treat an empty
 *  history as "no sub-steps to show", never as an error.
 *
 *  The response is JSONL, not JSON, so this parses line-by-line and
 *  skips malformed lines rather than failing the whole row. */
export const getTaskLogs = async (
  taskId: string,
  opts: { type?: string } = {},
): Promise<HarnTaskLogEntry[]> => {
  const qs = opts.type ? `?type=${encodeURIComponent(opts.type)}` : "";
  const raw = await request<unknown>(
    "GET",
    `/tasks/${encodeURIComponent(taskId)}/logs${qs}`,
    { taskIdForError: taskId },
  );
  // Tolerate all three shapes the endpoint may hand back: a raw JSONL
  // string, an already-parsed array, or an `{entries|logs:[...]}` wrap.
  if (typeof raw === "string") {
    return raw
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .flatMap((line) => {
        try {
          return [JSON.parse(line) as HarnTaskLogEntry];
        } catch {
          return [];
        }
      });
  }
  if (Array.isArray(raw)) return raw as HarnTaskLogEntry[];
  const wrapped = raw as { entries?: unknown; logs?: unknown } | null;
  const inner = wrapped?.entries ?? wrapped?.logs;
  return Array.isArray(inner) ? (inner as HarnTaskLogEntry[]) : [];
};

export const getTaskResult = (taskId: string) =>
  request<HarnTaskResultResponse>(
    "GET",
    `/tasks/${encodeURIComponent(taskId)}/result`,
    { taskIdForError: taskId },
  );

/** `DELETE /api/harn/tasks/:id` — cancel. Returns a typed envelope
 *  that distinguishes the "successfully canceled" path from the
 *  "already terminal" path (harn-service conflates these as a single
 *  404 `not_found_or_terminal` — see file header). */
export interface CancelTaskResult {
  cancelled: boolean;
  alreadyTerminal: boolean;
}

export const cancelTask = async (
  taskId: string,
): Promise<CancelTaskResult> => {
  try {
    const out = await request<{ cancelled: boolean }>(
      "DELETE",
      `/tasks/${encodeURIComponent(taskId)}`,
      { taskIdForError: taskId },
    );
    return { cancelled: Boolean(out.cancelled), alreadyTerminal: false };
  } catch (err) {
    // 404 `not_found_or_terminal` is treated as a successful
    // terminal-by-deletion (server's view: there's nothing to
    // cancel). The pane SHOULD surface this as a non-error.
    if (
      err instanceof HarnApiError &&
      err.status === 404 &&
      (err.code === "not_found_or_terminal" || err.code === "not_found")
    ) {
      return { cancelled: false, alreadyTerminal: true };
    }
    throw err;
  }
};

export const harnHealth = () =>
  request<{ ok: true; counts?: Record<string, number> }>("GET", "/health");
